// Pull to refresh, the DOM half (main.ts only): touch listeners, the spinner, the bounce held back.
// The rules and the maths are in web/src/pull.ts.
//
// Smoothness: touchmove only records the finger; one requestAnimationFrame per frame writes the
// transform (GPU, no layout). Where the browser supports overscroll-behavior (Safari 16+), the
// page's own bounce is off in CSS (html, body: overscroll-behavior-y none) and every listener is
// passive. Older browsers get one non-passive touchmove that cancels the bounce while pulling.
import { indicatorStyle, PULL, PullState, remainingSpin } from './pull';

/**
 * Wire pull to refresh. `allowed()` says whether this screen may pull now (main.ts: a top-level
 * tab, signed in, no sheet or drawer open, not dragging); `refresh()` reloads.
 */
export function initPull(allowed: () => boolean, refresh: () => Promise<unknown>) {
  const indicator = document.createElement('div');
  indicator.className = 'ptr';
  indicator.setAttribute('aria-hidden', 'true');
  indicator.innerHTML = '<span class="spin"></span>';
  document.body.prepend(indicator);
  const spin = indicator.firstElementChild as HTMLElement;
  const state = new PullState();
  const overscrollOff = typeof CSS !== 'undefined' && CSS.supports('overscroll-behavior-y', 'none');
  let busy = false;
  let frame = 0;
  let next = 0;
  let shown = false;
  const atTop = () => (document.scrollingElement?.scrollTop ?? window.scrollY) <= 0;
  const dragging = () => document.documentElement.classList.contains('is-dragging');

  const paint = (distance: number) => {
    const s = indicatorStyle(distance);
    indicator.style.transform = `translate3d(0, ${s.y}px, 0) scale(${s.scale})`;
    indicator.style.opacity = String(s.opacity);
    spin.style.transform = `rotate(${s.rotate}deg)`;
    indicator.classList.toggle('ready', s.ready);
  };
  /** Follow the finger: the latest distance, written once per frame. */
  const follow = (distance: number) => {
    shown = true;
    next = distance;
    if (!frame)
      frame = requestAnimationFrame(() => {
        frame = 0;
        paint(next);
      });
  };
  const cancelFrame = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };
  /** Ease to a resting spot with the CSS transition (.ptr.easing). */
  const easeTo = (distance: number) => {
    cancelFrame();
    indicator.classList.add('easing');
    paint(distance);
  };
  const hide = () => {
    if (!shown) return;
    shown = false;
    easeTo(0);
    indicator.classList.remove('busy', 'ready');
    window.setTimeout(() => {
      if (!busy) indicator.classList.remove('easing');
    }, 300);
  };
  const blocked = (target: EventTarget | null) =>
    target instanceof Element && !!target.closest('input, textarea, select, [data-grip], .sheet, .drawer, [contenteditable]');

  document.addEventListener(
    'touchstart',
    (e) => {
      const ok = !busy && e.touches.length === 1 && !blocked(e.target) && !dragging() && allowed();
      state.start(e.touches[0].clientY, atTop(), ok);
      if (ok) indicator.classList.remove('easing');
    },
    { passive: true },
  );
  const onMove = (e: TouchEvent) => {
    if (dragging()) return void state.end(true);
    const r = state.move(e.touches[0].clientY, atTop());
    if (!r.prevent) return;
    if (!overscrollOff && e.cancelable) e.preventDefault();
    follow(r.distance);
  };
  // Passive where the CSS already stops the bounce; else cancel it here while pulling.
  if (overscrollOff) document.addEventListener('touchmove', onMove, { passive: true });
  else document.addEventListener('touchmove', onMove, { passive: false });

  const finish = (cancelled: boolean) => {
    if (busy) return;
    if (state.end(cancelled) !== 'refresh') return hide();
    busy = true;
    shown = true;
    const startedAt = performance.now();
    easeTo(PULL.HOLD);
    indicator.classList.add('busy', 'ready');
    spin.style.transform = '';
    void refresh()
      .catch(() => {})
      .then(() => new Promise((done) => window.setTimeout(done, remainingSpin(startedAt, performance.now()))))
      .then(() => {
        busy = false;
        hide();
      });
  };
  document.addEventListener('touchend', () => finish(false), { passive: true });
  document.addEventListener('touchcancel', () => finish(true), { passive: true });
}
